import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import RadioButtonField from '../inputs/RadioButtonField';
import {log} from '@elixir/logger';
import styles from './Modal-hashed.scss';
import defaultTranslator from '@elixir/translator';
import getIconClasses from '../../iconUtils';
import { withModal } from './Modal';
import { withTheme } from '@elixir/theme';
import Button from '../button';

/**
 * The icons supported by the SimpleModalContent component
 *
 * @type {{WARNING_ICON: string, ERROR_ICON: string, INFO_ICON: string, EMAIL_ICON: string, QUESTION_ICON: string, SUCCESS_ICON: string}}
 */
const icons = {
    WARNING_ICON: 'gw-messageWarningIcon',
    ERROR_ICON: 'gw-messageErrorIcon',
    INFO_ICON: 'gw-messageInformationIcon',
    EMAIL_ICON: 'gw-messageEmailIcon',
    QUESTION_ICON: 'gw-messageQuestionIcon',
    SUCCESS_ICON: 'gw-messageSuccessIcon'
};

export {icons as ICON_TYPES};

/**
 * The `SimpleModalContent` component is the default message body component for the `ModalService`.  It supports six
 * different optional icon displays, as well as an optional cancel button, optional radio options, optional checkbox and
 * optional additional content.  All of these options can be configured via various methods in the `ModalService`.
 * This component is not meant to be directly imported and used by developers.
 *
 * This was adapted from:
 *   `.../frontend/js/src/edge/platform/widgets/modal/templates/message-template.html`
 * and
 *   `.../frontend/js/src/edge/platform/widgets/modal/ModalCtrl.js`
 *
 * Properties: {@link SimpleModalContent.propTypes}
 */
export class SimpleModalContent extends Component {
    /**
     * The default ID of a checkbox input if not provided in the `checkboxOptions`
     *
     * @type {string}
     */
    static DEFAULT_CHKBOX_ID = 'chkBox';
    /**
     * The icon class types supported by the SimpleModalContent component
     *
     * @type {{WARNING_ICON: string, ERROR_ICON: string, INFO_ICON: string, EMAIL_ICON: string, QUESTION_ICON: string, SUCCESS_ICON: string}}
     */
    static ICONS = icons;
    
    static propTypes = {
        /**
         * The title of the modal content
         */
        title: PropTypes.string.isRequired,
        /**
         * The message for the modal content
         */
        message: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
        /**
         * The label for the action button, defaults to `Ok` if not provided
         */
        actionLabel: PropTypes.string.isRequired,
        /**
         * The CSS class to use for the action button, defaults to `Modal-hashed__buttonPrimary__*`
         */
        actionBtnClass: PropTypes.string.isRequired,
        /**
         * If provided, is one of the four supported icons
         */
        iconClassType: PropTypes.oneOf([
            icons.WARNING_ICON,
            icons.ERROR_ICON,
            icons.INFO_ICON,
            icons.EMAIL_ICON,
            icons.QUESTION_ICON,
            icons.SUCCESS_ICON
        ]),
        /**
         * The optional description to add to the modal content
         */
        description: PropTypes.string,
        /**
         * The optional list of options used to render the modal content's RadioButtonField
         */
        radioOptions: PropTypes.array,
        /**
         * The optional object describing how to display the modal content's checkbox
         */
        checkboxOptions: PropTypes.object,
        /**
         * Optional flag indicating whether the cancel button is rendered
         */
        showCancelBtn: PropTypes.bool,
        /**
         * Optional label for the cancel button when rendered
         */
        cancelBtnLabel: PropTypes.string,
        /**
         * Any additional content to be added to tbe modal content
         */
        additionalContent: PropTypes.node,
        /**
         * Function from Modal which is called when "cancel" button is clicked
         */
        dismissAction: PropTypes.func,
        /**
         * Function from Modal called when "action" button is clicked
         */
        closeAction: PropTypes.func
    };

    static defaultProps = {
        actionLabel: {id: 'platform.modal.ModalService.Ok', defaultMessage: 'Ok'},
        actionBtnClass: styles.buttonPrimary,
        showCancelBtn: false,
        cancelBtnLabel: {id: 'platform.modal.ModalService.Close', defaultMessage: 'Close'}
    };

    /**
     * Define context properties that are set somewhere higher in the component chain.
     * Useful for passing down functions and settings without having to pass down as a prop.
     * Used here to obtain the translator function for localizing strings
     *
     * @type {Object}
     * @property {function(key: string)} translator - The function used to translate strings
     */
    static contextTypes = {
        translator: PropTypes.func.isRequired
    };

    /**
     * Constructs the SimpleModalContent component.  Checks the `radioOptions` and `checkboxOptions` props and creates
     * an appropriate `optionsModel` in the state for which ever one may be specified.
     *
     * @param {Object} props - The initial props for the component
     */
    constructor(props) {
        super(props);

        let optionsModel;
        const {radioOptions, checkboxOptions, iconClassType} = props;
        if (radioOptions && radioOptions.length) {
            optionsModel = {
                aspects: {
                    visible: true,
                    get valid() {
                        return !!optionsModel.value;
                    }
                }
            };
            optionsModel.value = null;
        }
        if (checkboxOptions) {
            if (optionsModel) {
                log.warning('optionsModel was already created for radioOptions and is being overridden for checkboxOptions');
            }
            optionsModel = {
                value: checkboxOptions.value
            };
        }
        if (iconClassType === icons.EMAIL_ICON) {
            if (optionsModel) {
                log.warning('optionsModel was already created for another prop and is being overridden for email form');
            }
            optionsModel = {
                value: ''
            };
        }
        this.state = {optionsModel};
    }

    /**
     * Callback function for the onChange of the email input.  Stops the event propagation and then updated the
     * `optionsModel.value` with the new target value
     *
     * @param {Object} evt - The event generated by the "change" of the checkbox
     */
    handleEmail = evt => {
        const {optionsModel} = this.state;
        evt.stopPropagation();
        optionsModel.value = evt.target.value;
        this.forceUpdate();
    };

    /**
     * Callback function for the onValueChange of the radio select field.  Simply updates the model with the new value.
     *
     * @param {*} value - The new value for the radio field
     * @param {Object} model - The model for the radio field
     */
    updateRadioModel = (value, model) => {
        model.value = value;
        this.forceUpdate();
    };

    /**
     * Callback function for the onChange of the checkbox input.  Stops the event propagation and then updated the
     * `optionsModel.value` with the new value of the target's checked property
     *
     * @param {Object} evt - The event generated by the "change" of the checkbox
     */
    handleCheckChange = evt => {
        const {optionsModel} = this.state;
        evt.stopPropagation();
        optionsModel.value = evt.target.checked;
        this.forceUpdate();
    };

    /**
     * Callback function for the "action" button. Stops the event propagation and then calls the `closeAction`
     * callback with the `optionsModel.value` if it exists.
     *
     * @param {Object} evt - The event generated by the "action" button
     */
    handleClose = evt => {
        const { closeAction } = this.props;
        const {optionsModel} = this.state;
        evt.stopPropagation();
        closeAction(optionsModel && optionsModel.value);
    };

    /**
     * Callback function for the "cancel" button.  Stops the event propagation and then calls the `dismissAction` callback
     *
     * @param {Object} evt - The event generated by the "cancel" button
     */
    handleDismiss = evt => {
        const { dismissAction } = this.props;
        evt.stopPropagation();
        dismissAction();
    };

    /**
     * Renders the header for the modal content's body.  The header changes colour depending on icon class type
     *
     * @returns {ReactElement} - The rendered header
     */
    renderHeader() {
        const {iconClassType} = this.props;
        const headerClass = cx(
            styles.modalHeader,
            {[styles.modalStateWarning]: iconClassType === icons.WARNING_ICON}, // for clean up: use color helper classes
            {[styles.modalStateError]: iconClassType === icons.ERROR_ICON},
            {[styles.modalStateInfo]: [icons.INFO_ICON, icons.EMAIL_ICON, icons.QUESTION_ICON, icons.SUCCESS_ICON].includes(iconClassType)},
        );
        return (
            <div className={headerClass}/>
        );
    }

    /**
     * Renders the icons for the body based on the `iconClassType`.
     *
     * @returns {ReactElement} - The rendered icons for the body
     */
    renderBodyIcons() {
        const {iconClassType, icon} = this.props;
        const iconDivs = [];
        if (iconClassType) {
            const icon1Class = cx(
                styles.iconSpan,
                getIconClasses('circle'),
                styles.faCircle,
                {[styles.modalStateWarningCircle]: iconClassType === icons.WARNING_ICON},
                {[styles.modalStateErrorCircle]: iconClassType === icons.ERROR_ICON},
                {[styles.modalStateInfoCircle]: [icons.INFO_ICON, icons.EMAIL_ICON, icons.QUESTION_ICON, icons.SUCCESS_ICON].includes(iconClassType)},
                'fa-stack-2x',
            );
            const icon2Class = cx(
                styles.iconSpan,
                {[getIconClasses('exclamation-triangle')]: iconClassType === icons.WARNING_ICON && !icon},
                {[getIconClasses('minus-circle')]: iconClassType === icons.ERROR_ICON && !icon},
                {[getIconClasses('info-circle')]: iconClassType === icons.INFO_ICON && !icon},
                {[getIconClasses('question-circle')]: iconClassType === icons.QUESTION_ICON && !icon},
                {[getIconClasses('check-circle')]: iconClassType === icons.SUCCESS_ICON && !icon},
                {[getIconClasses('far envelope')]: iconClassType === icons.EMAIL_ICON && !icon},
                'fa-stack-1x',
                {[styles.faExclamationTriangle]: iconClassType === icons.WARNING_ICON}, // for clean up: use color helper classes
                {[styles.faMinusCircle]: iconClassType === icons.ERROR_ICON},
                {[styles.faInfoCircle]: iconClassType === icons.INFO_ICON},
                {[styles.faEnvelope]: iconClassType === icons.EMAIL_ICON},
                icon
            );
            iconDivs[0] = <div key="icon1"><i className={icon1Class}/></div>;
            iconDivs[1] = <div key="icon2"><i className={icon2Class}/></div>;
        }
        return (
            <div className={styles.iconWrapper}>
                {iconDivs}
            </div>
        );
    }

    /**
     * Renders an email form on the page when the EMAIL_ICON is specified
     *
     * @returns {ReactElement|null} - The rendered email form if specified, otherwise null
     */
    renderEmailForm() {
        const {iconClassType} = this.props;
        const translator = this.context.translator || defaultTranslator;
        if (iconClassType !== icons.EMAIL_ICON) {
            return null;
        }
        return (
            <div>
                <form className={styles.modalContent}>
                    <input
                        className={styles.modalEmailField}
                        type="email"
                        name="email"
                        onChange={this.handleEmail}
                        placeholder={translator({
                            id: 'platform.modal.message-template.Please enter your email address:',
                            defaultMessage: 'Please enter your email address'
                        })}
                    />
                </form>
            </div>
        );
    }

    /**
     * Renders the radio options for the modal content's body using a RadioButtonField
     *
     * @returns {ReactElement|null} - The rendered radio options if specified, otherwise null
     */
    renderRadioOptions() {
        const {radioOptions} = this.props;
        const {optionsModel} = this.state;
        if (!radioOptions || !radioOptions.length) {
            return null;
        }
        const value = optionsModel.value;
        return (
            <div className={styles.modalTranscludedContent}>
                <RadioButtonField
                    id="modalRadios"
                    availableValues={radioOptions}
                    model={optionsModel}
                    value={value}
                    onValueChange={this.updateRadioModel}
                />
            </div>
        );
    }

    /**
     * Renders the checkbox options for the modal content's body.
     *
     * @returns {ReactElement|null} - The rendered checkbox options if specified, otherwise null
     */
    renderCheckboxOptions() {
        const {checkboxOptions} = this.props;
        if (!checkboxOptions) {
            return null;
        }
        const {id = SimpleModalContent.DEFAULT_CHKBOX_ID, label} = checkboxOptions;
        const {optionsModel} = this.state;
        const translator = this.context.translator || defaultTranslator;
        const value = optionsModel.value;
        const checked = value == true; // eslint-disable-line eqeqeq
        return (
            <div>
                <div className="gw-modal__checkbox-option">
                    <label htmlFor={id}>
                        {translator(label)}
                        <input type="checkbox" id={id} value={value || ''} checked={checked} onChange={this.handleCheckChange}/>
                    </label>
                </div>
            </div>
        );
    }

    /**
     * Renders the body portion of the modal content.  The body contains: the icons; heading with the `title`; the
     * `message` (and optional `description`); an email form IF the icon type is for email; any radio options if
     * specified; any checkbox options if specified; and any additional content.
     *
     * @returns {ReactElement} - The rendered body
     */
    renderBody() {
        const {
            title, message, description, additionalContent
        } = this.props;
        const translator = this.context.translator || defaultTranslator;
        const descParagraph = !description ? null : <p className={styles.description}>{translator(description)}</p>;
        const additionalContentDiv = !additionalContent ? null : (
            <div id="transcludedContent" className={styles.modalTranscludedContent}>{additionalContent}</div>
        );
        return (
            <div className={styles.modalBody}>
                <div className={styles.modalIcon}>
                    {this.renderBodyIcons()}
                </div>
                <div className={styles.modalTitle}>
                    {translator(title)}
                </div>
                <div className={styles.message}>
                    <p>{translator(message)}</p>
                    {descParagraph}
                </div>
                {this.renderEmailForm()}
                {this.renderRadioOptions()}
                {this.renderCheckboxOptions()}
                {additionalContentDiv}
            </div>
        );
    }

    /**
     * Renders the footer portion of the modal content containing the "action" button and an optional "cancel" button.
     *
     * @returns {ReactElement} - The rendered footer
     */
    renderFooter() {
        const {
            showCancelBtn, cancelBtnLabel, actionLabel, actionBtnClass
        } = this.props;
        const translator = this.context.translator || defaultTranslator;
        const cancelButton = !showCancelBtn ? null : (
            <Button onClick={this.handleDismiss} type='tertiary' className={styles.modalButton}>
                {translator(cancelBtnLabel)}
            </Button>
        );
        const okButtonClass = cx(
            styles.modalButton,
            actionBtnClass
        );
        return (
            <div className={styles.modalFooter}>
                {cancelButton}
                <Button onClick={this.handleClose} className={okButtonClass}>
                    {translator(actionLabel)}
                </Button>
            </div>
        );
    }

    /**
     * Renders the SimpleModalContent component's header, body and footer
     *
     * @returns {ReactElement} - The rendered component
     */
    render() {
        return (
            <div>
                {this.renderHeader()}
                {this.renderBody()}
                {this.renderFooter()}
            </div>
        );
    }
}

export default withTheme(withModal(SimpleModalContent));
